/**
 * Location search, geolocation, and saved sites (SPEC §6).
 * Saved sites live in localStorage — no accounts, no backend.
 */

import { useEffect, useRef, useState } from 'react';
import { geocode, parseLatLon, zoomForKind, type GeocodeResult } from '../lib/geocode';
import { useStore, type SavedSite } from '../state/store';

const STORAGE_KEY = 'terrain-airflow.sites';

export function loadSites(): SavedSite[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as SavedSite[]) : [];
  } catch {
    return [];
  }
}

export function persistSites(sites: SavedSite[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sites));
  } catch {
    // Private browsing or a full quota; saved sites are a convenience, not
    // something worth failing the app over.
  }
}

export function SiteSearch() {
  const set = useStore((s) => s.set);
  const sites = useStore((s) => s.sites);
  const lat = useStore((s) => s.lat);
  const lon = useStore((s) => s.lon);
  const zoom = useStore((s) => s.zoom);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GeocodeResult[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    const coords = parseLatLon(q);
    if (coords) {
      setResults([
        {
          name: `${coords.lat.toFixed(4)}, ${coords.lon.toFixed(4)}`,
          detail: 'Coordinates',
          lat: coords.lat,
          lon: coords.lon,
          kind: 'peak',
        },
      ]);
      setOpen(true);
      return;
    }

    const t = setTimeout(async () => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setBusy(true);
      try {
        const r = await geocode(q, ctrl.signal, { lat, lon });
        setResults(r);
        setOpen(true);
      } catch (err) {
        if ((err as Error).name !== 'AbortError') setResults([]);
      } finally {
        setBusy(false);
      }
    }, 260);

    return () => clearTimeout(t);
  }, [query, lat, lon]);

  const go = (r: GeocodeResult) => {
    set({ lat: r.lat, lon: r.lon, zoom: zoomForKind(r.kind) });
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        set({ lat: pos.coords.latitude, lon: pos.coords.longitude, zoom: 13 }),
      () => undefined,
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const saveHere = () => {
    const name = window.prompt('Name this site', 'New site');
    if (!name) return;
    const next = [
      ...sites,
      { id: `${Date.now()}`, name, lat, lon, zoom },
    ];
    set({ sites: next });
    persistSites(next);
  };

  const removeSite = (id: string) => {
    const next = sites.filter((s) => s.id !== id);
    set({ sites: next });
    persistSites(next);
  };

  return (
    <section className="panel search-panel">
      <div className="search-row">
        <input
          type="search"
          value={query}
          placeholder="Search a site, peak or place…"
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 160)}
          aria-label="Search for a location"
        />
        <button onClick={useMyLocation} title="Use my location" aria-label="Use my location">
          ◎
        </button>
      </div>

      {open && (busy || results.length > 0) && (
        <ul className="results">
          {busy && results.length === 0 && <li className="muted">Searching…</li>}
          {results.map((r, i) => (
            <li key={`${r.lat},${r.lon},${i}`}>
              <button onMouseDown={() => go(r)}>
                <strong>{r.name}</strong>
                {r.detail && <span>{r.detail}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="sites-row">
        <button className="link-btn" onClick={saveHere}>
          + Save this view
        </button>
      </div>

      {sites.length > 0 && (
        <ul className="site-list">
          {sites.map((site) => (
            <li key={site.id}>
              <button
                className="site-go"
                onClick={() => set({ lat: site.lat, lon: site.lon, zoom: site.zoom })}
              >
                {site.name}
              </button>
              <button
                className="site-del"
                onClick={() => removeSite(site.id)}
                aria-label={`Remove ${site.name}`}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
